// apps/display/src/effects/effectComponents.tsx
// Phase 6 — Score Animations / Task 7: built-in SAMPLE effect components.
// Full-screen overlay visuals rendered over the scoreboard. Each component
// calls onDone() once after EFFECT_DURATION[type] ms. Player-centric effects
// resolve the real player via resolvePlayerLine (board format, never hardcoded).

import { useEffect, useState } from 'react';
import type { GameEffect, GameEffectType } from './types';
import type { GameSnapshot } from '../socket';
import { ParticleCanvas } from './ParticleCanvas';
import { useEffectTheme } from './effectThemeContext';
import { ensureKeyframes } from './keyframes';
import { resolvePlayerLine, type PlayerLine } from '../widget-engine/playerLine';
import { glowShadow } from './effectTheme';
import { useDisplayStore } from '../store';

export interface EffectProps {
  effect: GameEffect;
  snapshot: GameSnapshot | null;
  onDone: () => void;
}

// On-screen duration per effect (ms); the component calls onDone() after this.
export const EFFECT_DURATION: Record<GameEffectType, number> = {
  THREE_POINTER_MADE: 2200, SCORING_RUN: 3000, LEAD_CHANGE: 2400,
  TIE_GAME: 2000, PLAYER_MILESTONE: 3000, FOUL_OUT: 2400,
  BUZZER_BEATER: 2600, PERIOD_END: 3500, GAME_FINAL: 6000,
  DUNK: 2600, AND_ONE: 2200, TIMEOUT_TAKEOVER: 8000,
  MAKE_NOISE: 4000, DEFENSE_CHANT: 4000, CROWD_WAVE: 3000, SPONSOR_STINGER: 5000,
  PLAYER_SCORED: 1800,
};

// Board color conventions — now skin tokens (set by AnimationHost via fxVars).
const RED = 'var(--fx-alert)';
const GREEN = 'var(--fx-positive)';
const GOLD = 'var(--fx-celebrate)';
const AMBER = 'var(--fx-accent)';
const CYAN = 'var(--fx-accent)';
const WHITE = 'var(--fx-ink)';
const SUB = 'var(--fx-sub)';
const CARD_BG = 'var(--fx-surface)';

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
};

// Shared one-shot timer hook: call onDone() once after the effect's duration.
function useEffectTimer(type: GameEffectType, onDone: () => void): void {
  useEffect(() => {
    ensureKeyframes();
    const t = window.setTimeout(onDone, EFFECT_DURATION[type]);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function card(border: string, extra?: React.CSSProperties): React.CSSProperties {
  return {
    background: CARD_BG,
    border: `3px solid ${border}`,
    borderRadius: 18,
    padding: '28px 48px',
    boxShadow: `0 0 calc(40px * var(--fx-glow)) ${border}`,
    textAlign: 'center',
    color: WHITE,
    fontFamily: 'system-ui, sans-serif',
    ...extra,
  };
}

// Reusable board-format player line block (number + name + stat line).
function PlayerLineBlock({ line }: { line: PlayerLine }) {
  return (
    <div>
      <div style={{ fontSize: 44, fontWeight: 900, letterSpacing: 1 }}>
        <span style={{ color: RED, fontFamily: 'ui-monospace, monospace' }}>#{line.no}</span>
        <span style={{ color: WHITE, marginLeft: 16 }}>{line.name}</span>
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 10, letterSpacing: 1 }}>
        <span style={{ color: GREEN }}>PTS {line.pts}</span>
        <span style={{ color: SUB }}> · REB {line.reb} · AST {line.ast}</span>
      </div>
    </div>
  );
}

function resolveLine(effect: GameEffect, snapshot: GameSnapshot | null): PlayerLine | null {
  return effect.side && effect.playerId
    ? resolvePlayerLine(snapshot, snapshot?.state ?? null, effect.side, effect.playerId)
    : null;
}

// ---- 6A auto-set components -------------------------------------------------

function ThreePointerMade({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const fx = useEffectTheme();
  return (
    <div style={overlay}>
      <ParticleCanvas type="fire" y={0.6} />
      <ParticleCanvas type="burst" colors={fx?.particles} />
      <div
        style={{
          ...card(GOLD),
          animation: 'stampIn 0.5s cubic-bezier(.2,1.2,.3,1) both',
          fontSize: 96,
          fontWeight: 900,
          color: GOLD,
          letterSpacing: 4,
        }}
      >
        TRIPLE!
      </div>
    </div>
  );
}

function ScoringRun({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const run = typeof effect.data?.run === 'number' ? effect.data.run : null;
  const tint = effect.side === 'away' ? CYAN : RED;
  return (
    <div style={overlay}>
      <ParticleCanvas type="fire" y={0.7} />
      <div
        style={{
          ...card(tint),
          animation: 'runSlide 3s ease-in-out both',
          fontSize: 80,
          fontWeight: 900,
          color: tint,
          letterSpacing: 3,
        }}
      >
        {run !== null ? `${run}-0 RUN` : 'ON A RUN'}
      </div>
    </div>
  );
}

function LeadChange({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const tint = effect.side === 'away' ? CYAN : RED;
  return (
    <div style={overlay}>
      <div
        style={{
          ...card(tint),
          animation: 'drop 0.6s ease-out both',
          fontSize: 72,
          fontWeight: 900,
          color: tint,
          letterSpacing: 3,
        }}
      >
        LEAD CHANGE
      </div>
    </div>
  );
}

function TieGame({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  return (
    <div style={overlay}>
      <div
        style={{
          ...card(GOLD, { perspective: 600 }),
          animation: 'flipIn 0.6s ease-out both',
          fontSize: 76,
          fontWeight: 900,
          color: GOLD,
          letterSpacing: 4,
        }}
      >
        TIE GAME
      </div>
    </div>
  );
}

function prettyMilestone(raw: unknown): string {
  if (typeof raw !== 'string') return 'MILESTONE';
  return raw.replace(/_/g, ' ').toUpperCase();
}

function PlayerMilestone({ effect, snapshot, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const line = resolveLine(effect, snapshot);
  const milestone = prettyMilestone(effect.data?.milestone);
  const fx = useEffectTheme();
  return (
    <div style={overlay}>
      <ParticleCanvas type="gold" colors={fx ? [fx.celebrate] : undefined} />
      <div
        style={{
          ...card(GOLD),
          animation: 'spinIn 0.7s cubic-bezier(.2,1.1,.3,1) both',
        }}
      >
        <div style={{ fontSize: 64, fontWeight: 900, color: GOLD, letterSpacing: 2 }}>{milestone}</div>
        {line && (
          <div style={{ marginTop: 18 }}>
            <PlayerLineBlock line={line} />
          </div>
        )}
      </div>
    </div>
  );
}

function FoulOut({ effect, snapshot, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const line = resolveLine(effect, snapshot);
  return (
    <div style={overlay}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle, color-mix(in srgb, ${AMBER} 20%, transparent) 0%, transparent 70%)`,
          animation: 'glow 0.8s ease-in-out 2 alternate',
        }}
      />
      <div
        style={{
          ...card(AMBER),
          animation: 'slamIn 0.5s cubic-bezier(.2,1.2,.3,1) both',
        }}
      >
        {line && (
          <div style={{ marginBottom: 16 }}>
            <PlayerLineBlock line={line} />
          </div>
        )}
        <div style={{ fontSize: 72, fontWeight: 900, color: AMBER, letterSpacing: 3 }}>FOULED OUT</div>
      </div>
    </div>
  );
}

function BuzzerBeater({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const fx = useEffectTheme();
  return (
    <div style={overlay}>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg, #ffffff, ${GOLD})`,
          animation: 'zoomT 1.4s ease-out both',
        }}
      />
      <ParticleCanvas type="gold" colors={fx ? [fx.celebrate] : undefined} />
      <ParticleCanvas type="confetti" colors={fx?.particles} />
      <div
        style={{
          ...card(GOLD),
          animation: 'slamIn 0.55s cubic-bezier(.2,1.2,.3,1) both',
          fontSize: 84,
          fontWeight: 900,
          color: GOLD,
          letterSpacing: 3,
        }}
      >
        BUZZER BEATER
      </div>
    </div>
  );
}

function PeriodEnd({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const period = typeof effect.data?.period === 'number' ? effect.data.period : null;
  return (
    <div style={overlay}>
      <div
        style={{
          ...card(CYAN),
          animation: 'drop 0.6s ease-out both',
          fontSize: 72,
          fontWeight: 900,
          color: CYAN,
          letterSpacing: 3,
        }}
      >
        {period !== null ? `END OF Q${period}` : 'END OF PERIOD'}
      </div>
    </div>
  );
}

function GameFinal({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const home = typeof effect.data?.home === 'string' ? effect.data.home : null;
  const away = typeof effect.data?.away === 'string' ? effect.data.away : null;
  const fx = useEffectTheme();
  return (
    <div style={overlay}>
      <ParticleCanvas type="confetti" colors={fx?.particles} />
      <ParticleCanvas type="gold" colors={fx ? [fx.celebrate] : undefined} />
      <div
        style={{
          ...card(GOLD),
          animation: 'zoomIn 0.7s ease-out both',
        }}
      >
        <div style={{ fontSize: 110, fontWeight: 900, color: GOLD, letterSpacing: 6 }}>FINAL</div>
        {home && away && (
          <div style={{ fontSize: 32, fontWeight: 700, color: WHITE, marginTop: 12, letterSpacing: 1 }}>
            {home.toUpperCase()} vs {away.toUpperCase()}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Manual showmanship components (ANIM-2) --------------------------------

function Dunk({ effect, snapshot, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const line = resolveLine(effect, snapshot);
  const fx = useEffectTheme();
  return (
    <div style={{ ...overlay, animation: 'shk 0.55s ease' }}>
      <ParticleCanvas type="spark" colors={fx?.particles} />
      <ParticleCanvas type="burst" colors={fx?.particles} />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%,-50%)',
          width: 120,
          height: 120,
          borderRadius: '50%',
          border: `20px solid ${WHITE}`,
          animation: 'shockRing 0.7s ease-out forwards',
        }}
      />
      <div
        style={{
          ...card(RED),
          animation: 'slamIn 0.5s cubic-bezier(.2,1.2,.3,1) both',
        }}
      >
        <div style={{ fontSize: 110, fontWeight: 900, color: RED, letterSpacing: 4 }}>SLAM!</div>
        {line && (
          <div style={{ marginTop: 16 }}>
            <PlayerLineBlock line={line} />
          </div>
        )}
      </div>
    </div>
  );
}

function AndOne({ effect, snapshot, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const line = resolveLine(effect, snapshot);
  const fx = useEffectTheme();
  return (
    <div style={overlay}>
      <ParticleCanvas type="spark" colors={fx?.particles} />
      <div
        style={{
          ...card(GOLD),
          animation: 'stampIn 0.5s cubic-bezier(.2,1.2,.3,1) both',
        }}
      >
        <div style={{ fontSize: 92, fontWeight: 900, color: GOLD, letterSpacing: 3 }}>AND-1!</div>
        {line && (
          <div style={{ marginTop: 16 }}>
            <PlayerLineBlock line={line} />
          </div>
        )}
      </div>
    </div>
  );
}

function PlayerScoredEffect({ effect, snapshot, onDone }: EffectProps) {
  const flashableRows = useDisplayStore((s) => s.flashableRows);
  const setFlashPlayerId = useDisplayStore((s) => s.setFlashPlayerId);
  const pid = effect.playerId ?? '';
  const side = effect.side;
  const inBoard =
    !!pid &&
    (side
      ? flashableRows[side].includes(pid)
      : flashableRows.home.includes(pid) || flashableRows.away.includes(pid));
  const line = resolveLine(effect, snapshot);

  useEffect(() => {
    ensureKeyframes();
    if (inBoard) {
      setFlashPlayerId(pid);
      const clear = window.setTimeout(() => setFlashPlayerId(null), EFFECT_DURATION.PLAYER_SCORED);
      const done = window.setTimeout(onDone, EFFECT_DURATION.PLAYER_SCORED);
      return () => {
        window.clearTimeout(clear);
        window.clearTimeout(done);
        setFlashPlayerId(null);
      };
    }
    const done = window.setTimeout(onDone, EFFECT_DURATION.PLAYER_SCORED);
    return () => window.clearTimeout(done);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (inBoard || !line) return null; // row flash handled by the player table

  const pts = typeof effect.data?.points === 'number' ? effect.data.points : 2;
  const teamColor = side === 'away' ? 'var(--fx-away)' : 'var(--fx-home)';
  return (
    <div style={overlay}>
      <div style={card(teamColor, { padding: '18px 30px', textAlign: 'center', animation: 'zoomIn 0.4s ease-out both' })}>
        <div style={{ fontSize: '2.2vh', fontWeight: 800, color: WHITE }}>
          #{line.no} {line.name}
        </div>
        <div style={{ fontSize: '5vh', fontWeight: 900, color: GREEN }}>+{pts}</div>
        <div style={{ fontSize: '1.6vh', color: 'var(--fx-sub)' }}>{line.pts} PTS</div>
      </div>
    </div>
  );
}

function TimeoutTakeover({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const sponsor = typeof effect.data?.sponsor === 'string' ? effect.data.sponsor : 'YOUR SPONSOR HERE';
  return (
    <div style={{ ...overlay, background: 'rgba(0,0,0,0.85)' }}>
      <div
        style={{
          ...card(GOLD, { padding: '56px 96px', borderWidth: 4 }),
          animation: 'zoomIn 0.7s ease-out both',
        }}
      >
        <div style={{ fontSize: 30, fontWeight: 800, color: WHITE, letterSpacing: 6 }}>TIMEOUT</div>
        <div style={{ fontSize: 14, color: SUB, letterSpacing: 4, margin: '14px 0 16px' }}>
          BROUGHT TO YOU BY
        </div>
        <div
          style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: 14,
            padding: '24px 56px',
            border: `2px solid ${GOLD}`,
            boxShadow: `inset 0 0 24px rgba(0,0,0,0.8), ${glowShadow(GOLD)}`,
          }}
        >
          <div style={{ fontSize: 48, fontWeight: 900, color: GOLD, letterSpacing: 3, textShadow: glowShadow(GOLD) }}>
            {sponsor}
          </div>
        </div>
      </div>
    </div>
  );
}

function MakeNoise({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const fx = useEffectTheme();
  // Climbing decibel meter via CSS width transition (no new keyframes needed).
  const [level, setLevel] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setLevel((v) => Math.min(100, v + 12)), 80);
    return () => window.clearInterval(id);
  }, []);
  const label = level >= 90 ? 'DEAFENING!' : level >= 60 ? 'LOUDER!' : "LET'S GO!";
  return (
    <div style={overlay}>
      <ParticleCanvas type="spark" colors={fx?.particles} />
      <div style={{ ...card(AMBER), animation: 'drop 0.5s ease-out both' }}>
        <div style={{ fontSize: 56, fontWeight: 900, color: WHITE, letterSpacing: 3, textShadow: glowShadow(AMBER) }}>
          📣 MAKE SOME NOISE!
        </div>
        <div
          style={{
            width: 360,
            height: 26,
            margin: '20px auto 0',
            borderRadius: 13,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          <div
            style={{
              width: `${level}%`,
              height: '100%',
              borderRadius: 13,
              transition: 'width 0.1s linear',
              background: `linear-gradient(90deg, ${GREEN}, ${GOLD} 60%, ${RED})`,
            }}
          />
        </div>
        <div style={{ fontSize: 14, color: GOLD, fontWeight: 700, letterSpacing: 2, marginTop: 8 }}>
          {label} {level} dB
        </div>
      </div>
    </div>
  );
}

function DefenseChant({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  return (
    <div style={overlay}>
      <div
        style={{
          animation: 'defPulse 0.6s ease-in-out infinite',
          fontSize: 120,
          fontWeight: 900,
          color: RED,
          letterSpacing: 6,
          fontFamily: 'system-ui, sans-serif',
          textShadow: `${glowShadow(RED)}, 0 4px 16px #000`,
        }}
      >
        DEFENSE!
      </div>
    </div>
  );
}

function CrowdWave({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  return (
    <div style={{ ...overlay, overflow: 'hidden' }}>
      <div
        style={{
          whiteSpace: 'nowrap',
          fontSize: 84,
          fontWeight: 900,
          color: GREEN,
          letterSpacing: 4,
          textShadow: glowShadow(GREEN),
          animation: 'waveMove 2.5s ease-in-out infinite',
        }}
      >
        🙌 DO THE WAVE! 🙌 STAND UP! 🙌
      </div>
    </div>
  );
}

function SponsorStinger({ effect, onDone }: EffectProps) {
  useEffectTimer(effect.type, onDone);
  const sponsor = typeof effect.data?.sponsor === 'string' ? effect.data.sponsor : 'YOUR SPONSOR HERE';
  const tagline = typeof effect.data?.tagline === 'string' ? effect.data.tagline : null;
  const fx = useEffectTheme();
  return (
    <div style={{ ...overlay, background: 'rgba(0,0,0,0.55)' }}>
      <ParticleCanvas type="gold" colors={fx ? [fx.celebrate] : undefined} />
      <div
        style={{
          ...card(GOLD, { borderWidth: 4 }),
          animation: 'spinIn 0.7s cubic-bezier(.2,1.1,.3,1) both',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 800, color: SUB, letterSpacing: 5 }}>
          BROUGHT TO YOU BY
        </div>
        <div
          style={{
            fontSize: 64,
            fontWeight: 900,
            color: GOLD,
            letterSpacing: 3,
            marginTop: 14,
            textShadow: glowShadow(GOLD),
          }}
        >
          {sponsor}
        </div>
        {tagline && (
          <div style={{ fontSize: 22, fontWeight: 600, color: WHITE, letterSpacing: 2, marginTop: 10 }}>
            {tagline}
          </div>
        )}
      </div>
    </div>
  );
}

export const EFFECT_COMPONENTS: Partial<Record<GameEffectType, React.ComponentType<EffectProps>>> = {
  THREE_POINTER_MADE: ThreePointerMade,
  SCORING_RUN: ScoringRun,
  LEAD_CHANGE: LeadChange,
  TIE_GAME: TieGame,
  PLAYER_MILESTONE: PlayerMilestone,
  FOUL_OUT: FoulOut,
  BUZZER_BEATER: BuzzerBeater,
  PERIOD_END: PeriodEnd,
  GAME_FINAL: GameFinal,
  DUNK: Dunk,
  AND_ONE: AndOne,
  TIMEOUT_TAKEOVER: TimeoutTakeover,
  MAKE_NOISE: MakeNoise,
  DEFENSE_CHANT: DefenseChant,
  CROWD_WAVE: CrowdWave,
  SPONSOR_STINGER: SponsorStinger,
  PLAYER_SCORED: PlayerScoredEffect,
};
